/**
 * preferencias.ts — Web data-layer for the SELF-SERVICE user preferences (R62),
 * backed by the `preferencias_usuario` row of the logged-in account. The admin
 * side (seeing another user's consent, clearing it) lives in `lib/usuarios.ts`.
 *
 * Today this carries ONE preference: the vocería (press) consent. The rest of
 * the Preferencias tab — publication alerts and friends — already has its
 * columns in that table and lands here when it ships.
 */

import { apiGet, apiPatch } from '@/lib/apiClient';

export const ESTADOS_CONSENTIMIENTO = ['aceptado', 'rechazado', 'sin_responder'] as const;
export type EstadoConsentimiento = (typeof ESTADOS_CONSENTIMIENTO)[number];

export interface PreferenciasUsuario {
	/**
	 * `null` is NOT a fourth answer: it means the question was never put to the
	 * user. `sin_responder` means it WAS asked and they closed it — which is why
	 * we never ask again, and why a dismissal never counts as a rejection.
	 */
	voceriaConsentimiento: EstadoConsentimiento | null;
	/** ISO date of that answer, or `null` while there is none. */
	voceriaConsentimientoAt: string | null;
}

/** GET /preferencias/me — never creates the row; missing row reads as defaults. */
export async function getMisPreferencias(): Promise<PreferenciasUsuario> {
	return apiGet<PreferenciasUsuario>('/preferencias/me');
}

/**
 * GET /preferencias/investigador/:slug — admin only: SOMEONE ELSE's preferences,
 * addressed by the profile slug the caller already has on screen.
 *
 * There is no write twin, and that is the point: an admin can see the answer but
 * never gives it. The only admin write is clearing it — `resetVoceriaUsuario` in
 * `lib/usuarios.ts` — so the researcher is asked again.
 */
export async function getPreferenciasDeInvestigador(
	slug: string,
): Promise<PreferenciasUsuario> {
	return apiGet<PreferenciasUsuario>(
		`/preferencias/investigador/${encodeURIComponent(slug)}`,
	);
}

/** PATCH /preferencias/me — record the vocería answer (creates the row if needed). */
export async function responderConsentimientoVoceria(
	estado: EstadoConsentimiento,
): Promise<PreferenciasUsuario> {
	return apiPatch<PreferenciasUsuario>('/preferencias/me', { voceriaConsentimiento: estado });
}

// ─── Copy shared by the profile modal, the Preferencias tab and the admin ────

/** Human label per stored answer. `null` (never asked) is labelled by each view. */
export const ESTADO_CONSENTIMIENTO_LABEL: Record<EstadoConsentimiento, string> = {
	aceptado: 'Autoriza',
	rechazado: 'No autoriza',
	sin_responder: 'Sin responder',
};

/**
 * Preferencias que YA tienen columna en `preferencias_usuario` y todavía no
 * tienen a nadie que las lea. Se listan igual —apagadas— en las dos vistas que
 * muestran el catálogo (el hub del dueño y la gestión de usuarios) para que las
 * dos digan lo mismo: hoy la única preferencia viva es el consentimiento de
 * vocería. Cuando una de estas se cablee, aparece sola en ambas.
 */
export const PREFERENCIAS_PROXIMAMENTE = [
	{
		id: 'notif-new-publication',
		titulo: 'Avisos de publicaciones nuevas',
		descripcion: 'Recibir un correo cuando se detecte una publicación tuya en WoS o Scopus.',
	},
	{
		id: 'notif-project-status',
		titulo: 'Cambios de estado en proyectos',
		descripcion: 'Recibir un correo cuando cambie el estado de un proyecto en el que participas.',
	},
	{
		id: 'notif-new-call',
		titulo: 'Nuevas convocatorias',
		descripcion: 'Recibir un correo cuando se abra una convocatoria de tu área.',
	},
] as const;

export const CONSENTIMIENTO_VOCERIA = {
	label: 'Vocerías científicas UDA',
	/** One-liner for the admin table and the preferences card. */
	descripcion:
		'Autoriza al Laboratorio de Comunicación Pública de la Ciencia a usar tus datos de contacto para entrevistas, cuñas, notas de prensa, contenidos científicos y actividades de divulgación de tu especialidad.',
} as const;
